import React, { useCallback, useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  increment,
} from "firebase/firestore";
import { db } from "../firebase/config";
import "./ChildrenTab.css";
import EditChildModal from "./EditChildModal";
import Emoji from "./Emoji";

function ChildrenTab() {
  const [children, setChildren] = useState([]);
  const [tasksByChild, setTasksByChild] = useState({});
  const [editingChild, setEditingChild] = useState(null);
  const [ordersByChild, setOrdersByChild] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});

  const loadOrders = useCallback(async (childIds) => {
    const snap = await getDocs(collection(db, "purchaseHistory"));
    const all = snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  
    const grouped = {};
    childIds.forEach(id => {
      grouped[id] = all
        .filter(order => order.child === id)
        .sort((a, b) => b.purchasedAt.toDate() - a.purchasedAt.toDate()) // 🔁 нові перші
        .slice(0, 5);
    });
  
    setOrdersByChild(grouped);
  }, []);

  const toggleOrders = (childId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [childId]: !prev[childId]
    }));
  };

  const loadTasks = useCallback(async (childIds) => {
    const q = query(collection(db, "taskHistory"), where("status", "==", "done"));
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const grouped = {};
    childIds.forEach(id => {
      grouped[id] = all.filter(task => task.child === id);
    });

    setTasksByChild(grouped);
  }, []);

  const loadChildren = useCallback(async () => {
    const q = query(collection(db, "users"), where("type", "==", "child"));
    const snapshot = await getDocs(q);
    const kids = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setChildren(kids);

    const ids = kids.map(k => k.id);
    loadTasks(ids);
    loadOrders(ids);
  }, [loadOrders, loadTasks]);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const confirmTask = async (task, childId) => {
    const taskRef = doc(db, "taskHistory", task.id);
    const userRef = doc(db, "users", childId);

    await updateDoc(taskRef, {
      status: "verified"
    });

    await updateDoc(userRef, {
      points: increment(Number(task.points || 0))
    });

    loadTasks(children.map(c => c.id));
  };

  const rejectTask = async (task) => {
    const taskRef = doc(db, "taskHistory", task.id);
    await updateDoc(taskRef, {
      status: "todo",
      completedAt: null
    });

    loadTasks(children.map(c => c.id));
  };

  const handleEdit = (child) => {
    setEditingChild(child);
  };

  const handleModalSaved = () => {
    loadChildren(); // оновити після збереження
  };

  return (
    <div className="children-tab">
      {children.map(child => (
        <div className="child-card" key={child.id}>
          <img src={child.photo} alt={child.name} className="child-avatar" />

          <div className="child-main">
            <div className="child-info">
              <h3>
                {child.name}
                <button className="edit-btn" onClick={() => handleEdit(child)}>
                  <Emoji label="Редагувати" symbol="✏️" />
                </button>
              </h3>
              <p><Emoji label="Кубок" symbol="🏆" /> {child.points || 0} балів</p>
            </div>

            {tasksByChild[child.id]?.length > 0 && (
              <div className="task-list">
                {tasksByChild[child.id].map(task => (
                  <div key={task.id} className="task-item">
                    <div className="task-title">
                      <Emoji label="Іконка завдання" symbol={task.icon || "📝"} /> {task.title} — {task.points} балів
                    </div>
                    <p>{task.description}</p>
                    <div className="task-actions">
                      <button onClick={() => confirmTask(task, child.id)}>Підтвердити</button>
                      <button className="reject" onClick={() => rejectTask(task)}>Відхилити</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button className="orders-btn" onClick={() => toggleOrders(child.id)}>
              Переглянути останні замовлення
            </button>

            {expandedOrders[child.id] && (
              <div className="order-list">
              {ordersByChild[child.id]?.length > 0 ? (
                ordersByChild[child.id].map(order => {
                  const purchaseDate = new Date(order.purchasedAt.seconds * 1000);
                  const isToday =
                    purchaseDate.toDateString() === new Date().toDateString();
            
                  return (
                    <div
                      key={order.id}
                      className={`order-item ${isToday ? "new-order" : ""}`}
                    >
                      <Emoji label="Дата" symbol="🗓" /> {purchaseDate.toLocaleDateString("uk-UA")},
                      <strong> {order.title}</strong> — {order.price} балів
                      {isToday && <span className="new-label"><Emoji label="Нове" symbol="🆕" /></span>}
                    </div>
                  );
                })
              ) : (
                <p>Поки немає покупок</p>
              )}
            </div>            
            )}
          </div>
        </div>
      ))}

      {editingChild && (
        <EditChildModal
          child={editingChild}
          onClose={() => setEditingChild(null)}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
}

export default ChildrenTab;
